import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { PortIn } from "./PortIn";
import { useStyle as useProductLineStyle } from "../../ProductLine/style";
import { ICart, ICartOffer, IPortin } from "../../ICart";
import { format } from "date-fns";
import { useGetPortin } from "../../hook/useGetPortin";
import { ESimDeviceForm } from "@features/cart/components/PortIn/ESimDeviceForm";
import { EEirSalesSimType } from "@mappers/getCartMapper";
import { usePostInclusiveEquipments } from "@features/cart/hook/usePostInclusiveEquipments";
import { useGetDeviceDetails } from "@features/cart/hook/useGetDeviceDetails";
import { MoreAboutEsim } from "./MoreAboutESim/MoreAboutESim";
import { ProductLineCheckbox } from "@features/cart/components/PortIn/ProductLineCheckbox";
import { useStyle as usePortInStyle } from "./style";
import { Card, CardContent, Collapse, Divider, Grid, IconButton, Typography } from "@mui/material";
import { cls } from "itsf-ui-common";
import { ErrorHelperText } from "@common/ErrorHelperText/ErrorHelperText";
import { useDeviceType } from "@hooks/useDeviceType";
import { useFormContext } from "react-hook-form";
import { useGetPortinSlot } from "@features/portIn/hook/useGetPortinSlot";

interface IParams {
    isPortOpen: boolean;
    userOffer: ICartOffer;
    addPortin(cartOfferId: number, portinForm: IPortin, closePortInForm?: () => void): void;
    deleteOffer(offerId: number): void;
    deletePortin(offerId: number): void;
    setIsPortOpen(isPortOpen: boolean): void;
    setCart(cart: ICart): void;
    hasError?: boolean;
    isPortInCardError?: boolean;
    setIsPortInCardError?(isPortInCardError: boolean): void;
    onPortInDisabled?(offerId: number): void;
}

export const ProductLine = (props: IParams) => {
    const {
        userOffer,
        deleteOffer,
        isPortOpen,
        setIsPortOpen,
        deletePortin,
        addPortin,
        setCart,
        hasError,
        isPortInCardError,
        setIsPortInCardError,
        onPortInDisabled,
    } = props;
    const { getPortinState, portinInfo } = useGetPortin({ cartOfferId: userOffer.offerId });
    const { getPortinSlot, getPortinSlotState, portinSlotList } = useGetPortinSlot(false);
    const { classes: productLineClasses } = useProductLineStyle();
    const { classes: portInClasses } = usePortInStyle();

    const { t } = useTranslation(["cart", "eSim"]);

    const { isMobile } = useDeviceType();
    const { clearErrors, setValue } = useFormContext();

    const [isESimFormOpen, setIsESimFormOpen] = useState(false);

    const { postInclusiveEquipment, postInclusiveEquipmentState } = usePostInclusiveEquipments({
        setCart: setCart,
    });

    const {
        deviceDetails,
        getDeviceDetailsState,
        postDeviceDetails,
        postDeviceDetailsState,
        deleteDeviceDetails,
        deleteDeviceDetailsState,
    } = useGetDeviceDetails({
        offerId: userOffer.offerId,
        isESimFormOpen,
    });

    const portInDate = userOffer.port ? format(new Date(userOffer.port.portStartDateTime), "dd/MM/yyyy") : "";
    const isPortInFormOpen = userOffer.port && !isPortOpen;
    const phoneNumberToPort = userOffer.port ? userOffer.port.phoneNumber : "";
    const togglePortIsSwitched = Boolean(userOffer.port) || isPortOpen;
    const hasPortInCardError = Boolean(isPortInCardError) && isPortOpen;

    const wantsESim = userOffer.simType === EEirSalesSimType.ESIM;

    const togglePortInForm = () => {
        if (userOffer.port) {
            deletePortin(userOffer.offerId);
            if (isPortOpen) {
                setIsPortOpen(false);
            }

            return;
        }

        if (isPortOpen) {
            setIsPortOpen(false);

            return;
        }

        setIsPortOpen(true);
        getPortinSlot();

        if (togglePortIsSwitched) {
            if (onPortInDisabled) {
                onPortInDisabled(userOffer.offerId);
            }
            clearErrors(`portIn.${userOffer.offerId}`);
            setValue(`portIn.${userOffer.offerId}.phoneNumber`, "");
            setValue(`portIn.${userOffer.offerId}.validationCode`, "");
            setValue(`portIn.${userOffer.offerId}.paymentType`, "");
            setValue(`portIn.${userOffer.offerId}.accountNumber`, "");
            setValue(`portIn.${userOffer.offerId}.date`, null);
            setValue(`portIn.${userOffer.offerId}.time`, "");
        }
    };

    const toggleESimForm = async () => {
        if (wantsESim) {
            await deleteDeviceDetails(userOffer.offerId);
            await postInclusiveEquipment(userOffer.offerId, EEirSalesSimType.PSIM);
            if (isESimFormOpen) {
                setIsESimFormOpen((prevState) => !prevState);
            }

            clearErrors(`eSim.${userOffer.offerId}`);
            setValue(`eSim.${userOffer.offerId}.brand`, "");
            setValue(`eSim.${userOffer.offerId}.eid`, "");
        } else {
            await postInclusiveEquipment(userOffer.offerId, EEirSalesSimType.ESIM);
            setIsESimFormOpen((prevState) => !prevState);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLLabelElement>) => {
        if (event.key === "Enter" || event.keyCode === 32) {
            togglePortInForm();
        }
    };

    const getCheckedDetailsComponent = () => {
        return (
            <>
                <Typography color="inherit" variant="normal">
                    {t("cart:phoneNumber")}:{" "}
                    <span className={portInClasses.checkedDetailsValue}>{phoneNumberToPort}</span>
                </Typography>
                <Typography color="inherit" variant="normal">
                    {t("cart:moveDate")}: <span className={portInClasses.checkedDetailsValue}>{portInDate}</span>
                </Typography>
            </>
        );
    };

    return (
        <div className="xtest_product_lines">
            <Card
                className={Boolean(hasError) || hasPortInCardError ? portInClasses.cardError : undefined}
                elevation={2}
            >
                <CardContent
                    className={cls(
                        productLineClasses.productLine,
                        isPortOpen ? productLineClasses.roundedBottom : undefined
                    )}
                >
                    <Grid alignItems="center" container justifyContent="space-between">
                        <Grid item xs={10}>
                            <Typography color="inherit" variant="h2">
                                {userOffer.baseOffer.description}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Grid alignItems="center" container justifyContent="flex-end">
                                <IconButton
                                    aria-label="Delete"
                                    className={cls(
                                        "xtest_delete_button",
                                        productLineClasses.deleteButton,
                                        productLineClasses.iconButton
                                    )}
                                    color="inherit"
                                    data-testid={`delete-offer-${userOffer.offerId}-icon`}
                                    size="large"
                                    onClick={() => deleteOffer(userOffer.offerId)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                    <ProductLineCheckbox
                        checkboxId={`portin-${userOffer.offerId.toString()}`}
                        checkboxTitle={t("cart:keepNumber")}
                        checkedDetailsComponent={isPortInFormOpen && getCheckedDetailsComponent()}
                        isChecked={togglePortIsSwitched}
                        isMobile={isMobile}
                        onChangeCheckbox={togglePortInForm}
                        onClickEditCheckedDetails={() => {
                            setIsPortOpen(true);
                            getPortinSlot();
                        }}
                        onKeyDown={handleKeyDown}
                    />
                </CardContent>
                <Collapse in={isPortOpen} timeout={300} unmountOnExit>
                    <PortIn
                        addPortin={addPortin}
                        closePortInForm={() => setIsPortOpen(false)}
                        getPortinSlot={getPortinSlot}
                        getPortinSlotState={getPortinSlotState}
                        getPortinState={getPortinState}
                        isPortInCardError={hasPortInCardError}
                        offerId={userOffer.offerId}
                        portinInfo={portinInfo}
                        portinSlotList={portinSlotList}
                        setIsPortInCardError={setIsPortInCardError}
                    />
                </Collapse>
                <Divider className={productLineClasses.divider} />
                <CardContent
                    className={cls(
                        productLineClasses.productLine,
                        productLineClasses.eSimContainer,
                        isPortOpen ? productLineClasses.roundedTop : undefined,
                        isESimFormOpen ? productLineClasses.roundedBottom : undefined
                    )}
                >
                    <div className={productLineClasses.switchContainer}>
                        <ProductLineCheckbox
                            checkboxId={`esim-${userOffer.offerId.toString()}`}
                            checkboxTitle={t("eSim:wantESim")}
                            isChecked={wantsESim}
                            isMobile={isMobile}
                            onChangeCheckbox={toggleESimForm}
                            onClickEditCheckedDetails={() => setIsESimFormOpen(true)}
                        />
                        <Typography className={productLineClasses.getESimDescription} variant="small">
                            {t("eSim:getESimDescription")} <MoreAboutEsim />
                        </Typography>
                    </div>
                    {wantsESim && (
                        <IconButton
                            aria-label="Edit"
                            className={cls("xtest_edit_button", productLineClasses.iconButton)}
                            color="inherit"
                            size="large"
                            onClick={() => setIsESimFormOpen((prev) => !prev)}
                        >
                            <EditIcon />
                        </IconButton>
                    )}
                </CardContent>
                <Collapse in={isESimFormOpen} timeout={300}>
                    <ESimDeviceForm
                        deleteDeviceDetails={deleteDeviceDetails}
                        deleteDeviceDetailsState={deleteDeviceDetailsState}
                        deviceDetails={deviceDetails}
                        deviceDetailsState={getDeviceDetailsState}
                        offerId={userOffer.offerId}
                        postDeviceDetails={postDeviceDetails}
                        postDeviceDetailsState={postDeviceDetailsState}
                        postInclusiveEquipmentState={postInclusiveEquipmentState}
                        setIsESimFormOpen={setIsESimFormOpen}
                    />
                </Collapse>
            </Card>
            {(hasPortInCardError || Boolean(hasError)) && (
                <ErrorHelperText isCardError message={t("cart:portInError")} />
            )}
        </div>
    );
};
